import React from 'react';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { useCountUp } from '../hooks/useCountUp';
import { STATS, TESTIMONIALS } from '../utils/constants';

const StatCard = ({ stat, index }) => {
  const { ref, count } = useCountUp({
    target: stat.number,
    duration: 2000,
    suffix: stat.suffix,
    observerOptions: { threshold: 0.5 },
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1, ease: 'easeOut' }}
      viewport={{ once: true }}
      style={{ textAlign: 'center' }}
    >
      <div style={{
        fontSize: 'clamp(2rem, 4vw, 3rem)',
        fontWeight: 700,
        color: '#1A1A1A',
        fontFamily: '"DM Sans", sans-serif',
        letterSpacing: '-0.02em',
        lineHeight: 1,
      }}>
        {count}
      </div>
      <div style={{
        fontSize: '0.875rem',
        color: '#666666',
        fontWeight: 500,
        marginTop: '0.5rem',
        fontFamily: '"DM Sans", sans-serif',
      }}>
        {stat.label}
      </div>
    </motion.div>
  );
};

const SocialProof = () => {
  const { ref: sectionRef, isVisible: sectionVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <>
      {/* Stats Section — cream background */}
      <section style={{
        backgroundColor: '#F5F0E8',
        padding: 'clamp(3rem, 6vw, 6rem) 1.5rem',
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '2rem',
          }} className="stats-grid">
            {STATS.map((stat, index) => (
              <StatCard key={stat.id} stat={stat} index={index} />
            ))}
          </div>
        </div>

        <style>{`
          @media (min-width: 768px) {
            .stats-grid {
              grid-template-columns: repeat(4, 1fr) !important;
              gap: 3rem !important;
            }
          }
        `}</style>
      </section>

      {/* Testimonials Section — dark background (Anthropic style) */}
      <section
        ref={sectionRef}
        style={{
          backgroundColor: '#111111',
          color: '#FFFFFF',
          padding: 'clamp(3rem, 6vw, 6rem) 1.5rem',
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={sectionVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            style={{
              fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
              fontWeight: 700,
              letterSpacing: '-0.02em',
              marginBottom: '3rem',
              color: '#FFFFFF',
              fontFamily: '"DM Sans", sans-serif',
            }}
          >
            What our clients are saying
          </motion.h2>

          <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            {TESTIMONIALS.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 20 }}
                animate={sectionVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.2 + index * 0.15, ease: 'easeOut' }}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: '1rem',
                  padding: '2.5rem 0',
                  borderBottom: '1px solid rgba(255,255,255,0.1)',
                }}
                className="testimonial-row"
              >
                {/* Left: source label */}
                <div>
                  <span style={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: 'rgba(255,255,255,0.6)',
                    fontFamily: '"DM Sans", sans-serif',
                    letterSpacing: '0.01em',
                  }}>
                    {testimonial.position}
                  </span>
                </div>

                {/* Right: quote and author */}
                <div>
                  <blockquote style={{
                    fontSize: '1rem',
                    lineHeight: 1.7,
                    color: 'rgba(255,255,255,0.75)',
                    fontFamily: '"DM Sans", sans-serif',
                    fontStyle: 'normal',
                    margin: 0,
                  }}>
                    &ldquo;{testimonial.quote}&rdquo;
                  </blockquote>
                  <div style={{ marginTop: '1rem' }}>
                    <span style={{
                      fontSize: '0.8rem',
                      color: 'rgba(255,255,255,0.35)',
                      fontFamily: '"DM Sans", sans-serif',
                    }}>
                      {testimonial.author}, {testimonial.location}
                    </span>
                  </div>
                  <button
                    style={{
                      marginTop: '0.75rem',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      color: 'rgba(255,255,255,0.5)',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      textUnderlineOffset: '3px',
                      fontFamily: '"DM Sans", sans-serif',
                    }}
                  >
                    Read story
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <style>{`
          @media (min-width: 768px) {
            .testimonial-row {
              grid-template-columns: 200px 1fr !important;
              gap: 3rem !important;
            }
          }
        `}</style>
      </section>
    </>
  );
};

export default SocialProof;
